import React, { useMemo } from 'react';
import { Eye, ChevronLeft, Database, MessageSquare, CheckCircle2, XCircle, Image } from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge';
import { type Question, getQuestionWithExplanation } from '../../../../content';
import { QuestionCardClean } from '../../../../practice/components/QuestionCardClean';
import { QuestionRendererFactory } from '../../../../practice/components/renderers/QuestionRendererFactory';
interface QuestionPreviewPanelProps {
  questionId: string | null;
  onBack?: () => void;
  accentColor?: 'blue' | 'purple' | 'emerald' | 'amber';
  showBackOnMobile?: boolean;
}
const ACCENT_COLORS = {
  blue: {
    bg: 'bg-blue-50',
    border: 'border-l-blue-500',
    text: 'text-blue-600'
  },
  purple: {
    bg: 'bg-purple-50',
    border: 'border-l-purple-500',
    text: 'text-purple-600'
  },
  emerald: {
    bg: 'bg-emerald-50',
    border: 'border-l-emerald-500',
    text: 'text-emerald-600'
  },
  amber: {
    bg: 'bg-amber-50',
    border: 'border-l-amber-500',
    text: 'text-amber-600'
  }
};
export function QuestionPreviewPanel({
  questionId,
  onBack,
  accentColor = 'blue',
  showBackOnMobile = true
}: QuestionPreviewPanelProps) {
  const question = useMemo(() => {
    if (!questionId) return null;
    return getQuestionWithExplanation(questionId);
  }, [questionId]);
  const colors = ACCENT_COLORS[accentColor];
  const visualCount = useMemo(() => {
    if (!question) return 0;
    return (question.stimulusAids?.length || 0) + (question.supportAids?.length || 0);
  }, [question]);
  // Empty state
  if (!question) {
    return <div className="flex-1 flex items-center justify-center text-gray-400 p-6 bg-slate-50" data-id="element-3603">
        <div className="text-center" data-id="element-3604">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4" data-id="element-3605">
            <Eye className="w-8 h-8 text-gray-300" data-id="element-3606" />
          </div>
          <p className="font-semibold text-gray-500" data-id="element-3607">Selecteer een vraag</p>
          <p className="text-sm mt-1 text-gray-400" data-id="element-3608">
            Kies een vraag uit de lijst om de preview te bekijken
          </p>
        </div>
      </div>;
  }
  return <div className="flex-1 min-h-0 overflow-y-auto bg-slate-50" style={{
    WebkitOverflowScrolling: 'touch'
  }} data-id="element-3609">
      <div className="p-4 sm:p-6 md:p-8 pb-8 sm:pb-12" data-id="element-3610">
        <div className="max-w-3xl mx-auto" data-id="element-3611">
          {/* Mobile back button */}
          {showBackOnMobile && onBack && <div className="mb-4 lg:hidden" data-id="element-3612">
              <button onClick={onBack} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors" data-id="element-3613">
                <ChevronLeft className="w-4 h-4" data-id="element-3614" />
                Terug naar lijst
              </button>
            </div>}

          {/* Question header badges */}
          <div className="mb-4 flex flex-wrap items-center gap-2" data-id="element-3615">
            <Badge variant="info" size="sm" data-id="element-3616">
              {question.questionType}
            </Badge>
            <span className="text-xs text-gray-500 font-mono truncate max-w-[200px]" data-id="element-3617">
              {question.familyId}
            </span>
            {visualCount > 0 && <div className="flex items-center gap-1.5 text-xs font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded-md border border-purple-100" data-id="element-3618">
                <Image className="w-3.5 h-3.5" data-id="element-3619" />
                {visualCount} visual{visualCount > 1 ? 's' : ''}
              </div>}
          </div>

          {/* Question Card */}
          <QuestionCardClean question={question} data-id="element-3620">
            <QuestionRendererFactory question={question} onAnswer={() => {}} data-id="element-3621" />
          </QuestionCardClean>

          {/* Metadata & Feedback Section */}
          <div className="mt-6 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3622">
            {/* Metadata Grid */}
            <div className="p-4 sm:p-5 border-b border-gray-100" data-id="element-3623">
              <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-3624">
                <Database className="w-4 h-4 text-gray-400" data-id="element-3625" />
                Vraag Metadata
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm" data-id="element-3626">
                <div data-id="element-3627">
                  <span className="block text-xs text-gray-500 mb-1" data-id="element-3628">
                    Skill
                  </span>
                  <span className="font-medium text-gray-800" data-id="element-3629">
                    {question.skill || '—'}
                  </span>
                </div>
                <div data-id="element-3630">
                  <span className="block text-xs text-gray-500 mb-1" data-id="element-3631">
                    SubSkill
                  </span>
                  <span className="font-medium text-gray-800" data-id="element-3632">
                    {question.subSkill || '—'}
                  </span>
                </div>
                <div data-id="element-3633">
                  <span className="block text-xs text-gray-500 mb-1" data-id="element-3634">
                    Level Code
                  </span>
                  <span className="font-medium text-gray-800" data-id="element-3635">
                    {question.levelCode || '—'}
                  </span>
                </div>
                <div data-id="element-3636">
                  <span className="block text-xs text-gray-500 mb-1" data-id="element-3637">
                    Groep / Tier
                  </span>
                  <span className="font-medium text-gray-800" data-id="element-3638">
                    Groep {question.group || '?'} · Tier {question.tier || '?'}
                  </span>
                </div>
              </div>
            </div>

            {/* Feedback Completeness */}
            <div className="p-4 sm:p-5 bg-gray-50/50" data-id="element-3639">
              <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5" data-id="element-3640">
                <MessageSquare className="w-3.5 h-3.5" data-id="element-3641" />
                Feedback Compleetheid
              </h5>
              <div className="flex flex-wrap gap-2" data-id="element-3642">
                <Badge variant={question.feedback?.explanationText ? 'success' : 'danger'} size="sm" data-id="element-3643">
                  {question.feedback?.explanationText ? <CheckCircle2 className="w-3 h-3 mr-1 inline" data-id="element-3644" /> : <XCircle className="w-3 h-3 mr-1 inline" data-id="element-3645" />}
                  Uitleg tekst
                </Badge>
                <Badge variant={question.feedback?.solutionSteps?.length ? 'success' : 'warning'} size="sm" data-id="element-3646">
                  {question.feedback?.solutionSteps?.length ? <CheckCircle2 className="w-3 h-3 mr-1 inline" data-id="element-3647" /> : <XCircle className="w-3 h-3 mr-1 inline" data-id="element-3648" />}
                  Stappenplan
                </Badge>
                <Badge variant={question.feedback?.distractorExplanations && Object.keys(question.feedback.distractorExplanations).length > 0 ? 'success' : 'warning'} size="sm" data-id="element-3649">
                  {question.feedback?.distractorExplanations && Object.keys(question.feedback.distractorExplanations).length > 0 ? <CheckCircle2 className="w-3 h-3 mr-1 inline" data-id="element-3650" /> : <XCircle className="w-3 h-3 mr-1 inline" data-id="element-3651" />}
                  Fout-specifieke feedback
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}